import { useState, useEffect, useRef, useCallback } from 'react';

const useChat = () => {
    const socketRef = useRef(null);
    const [messages, setMessages] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const reconnectTimeoutRef = useRef(null);

    const connect = useCallback(() => {
        // 기존 소켓이 있으면 닫고 초기화합니다.
        if (socketRef.current) {
            socketRef.current.close();
        }

        const apiBase = process.env.REACT_APP_API_BASE || '';
        let wsScheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
        let wsHost = window.location.host;

        if (apiBase.startsWith('http')) {
            const url = new URL(apiBase);
            wsScheme = url.protocol === 'https:' ? 'wss' : 'ws';
            wsHost = url.host;
        }

        const accessToken = localStorage.getItem('access');
        if (!accessToken) {
            console.warn('No access token found, skipping chat connection');
            return;
        }

        const wsUrl = `${wsScheme}://${wsHost}/ws/chat/?token=${accessToken}`;
        console.log('Connecting to WebSocket:', wsUrl);

        const ws = new WebSocket(wsUrl);
        socketRef.current = ws;

        ws.onopen = () => {
            console.log('Chat WebSocket connected');
            setIsConnected(true);
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        ws.onmessage = (event) => {
            const data = jsonParseSafe(event.data);
            if (data) {
                setMessages((prev) => [...prev, data]);
            }
        };

        ws.onclose = (e) => {
            console.log('Chat WebSocket disconnected', e.code);
            setIsConnected(false);

            // 정상 종료(1000, 1001)가 아닌 경우에만 3초 후 재연결 시도
            if (e.code !== 1000 && e.code !== 1001) {
                reconnectTimeoutRef.current = setTimeout(() => {
                    connect();
                }, 3000);
            }
        };

        ws.onerror = (err) => {
            console.error('Chat WebSocket error:', err);
            ws.close();
        };
    }, []);

    useEffect(() => {
        connect();
        return () => {
            if (socketRef.current) {
                socketRef.current.close();
                socketRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };
    }, [connect]);

    const sendMessage = useCallback((message, conversationId) => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify({
                message,
                conversation_id: conversationId,
            }));
        } else {
            console.warn('Chat WebSocket not connected or not open');
        }
    }, []);

    const jsonParseSafe = (data) => {
        try {
            return JSON.parse(data);
        } catch (e) {
            return null;
        }
    };

    return { messages, setMessages, sendMessage, isConnected };
};

export default useChat;
